import { useEffect, useMemo, useState } from 'react';
import { Calendar } from 'lucide-react';
import { differenceInCalendarDays, format, startOfDay } from 'date-fns';
import { PayrollPeriodSettings } from '../../lib/payrollPeriodSettings';
import { getUnrunPayrollPeriods, type UnrunPayrollPeriod } from '../../lib/payrollPastDuePeriods';
import { usePayrollHoursText } from '../../hooks/usePayrollHoursText';
import { payrollMinutesFromHours } from '../../lib/payrollHoursLabel';
import { voidPayrollPeriod } from '../../lib/payrollRuns';
import { toast } from 'react-hot-toast';

interface PayrollPeriodSelectorProps {
  businessId: string;
  payrollSettings: PayrollPeriodSettings | null;
  onPeriodSelected: (startDate: Date, endDate: Date) => void;
  onVoided?: () => void;
}

function periodKey(period: { start: Date; end: Date }): string {
  return `${format(period.start, 'yyyy-MM-dd')}_${format(period.end, 'yyyy-MM-dd')}`;
}

function daysLeftInPeriod(periodEnd: Date): number {
  return Math.max(0, differenceInCalendarDays(startOfDay(periodEnd), startOfDay(new Date())));
}

export default function PayrollPeriodSelector({
  businessId,
  payrollSettings,
  onPeriodSelected,
  onVoided,
}: PayrollPeriodSelectorProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [periods, setPeriods] = useState<UnrunPayrollPeriod[]>([]);
  const [isLoadingPeriods, setIsLoadingPeriods] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmingVoid, setConfirmingVoid] = useState(false);
  const [isVoiding, setIsVoiding] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const hoursText = usePayrollHoursText(periods.flatMap((period) => [
    payrollMinutesFromHours(period.straightHours),
    payrollMinutesFromHours(period.overtimeHours),
  ]));

  useEffect(() => {
    if (!payrollSettings) return;

    let cancelled = false;
    setIsLoadingPeriods(true);
    setLoadError(false);

    getUnrunPayrollPeriods(businessId, payrollSettings)
      .then((next) => {
        if (cancelled) return;
        setPeriods(next);
        setSelectedPeriod((current) => {
          if (current === 'custom') return current;
          if (next.some((period) => periodKey(period) === current)) return current;
          return next[0] ? periodKey(next[0]) : '';
        });
        setConfirmingVoid(false);
      })
      .catch((error) => {
        console.error('Error loading unrun payroll periods:', error);
        if (!cancelled) {
          setPeriods([]);
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPeriods(false);
      });

    return () => {
      cancelled = true;
    };
  }, [payrollSettings, businessId, reloadKey]);

  const usingCustomRange = selectedPeriod === 'custom' || !payrollSettings;

  const selectedPeriodDates = useMemo(() => {
    if (usingCustomRange) {
      if (!customStartDate || !customEndDate) return null;
      return {
        start: new Date(`${customStartDate}T00:00:00`),
        end: new Date(`${customEndDate}T23:59:59.999`)
      };
    }

    const option = periods.find((period) => periodKey(period) === selectedPeriod);
    if (!option) return null;

    return {
      start: option.start,
      end: option.end
    };
  }, [usingCustomRange, selectedPeriod, customStartDate, customEndDate, periods]);

  const handleLoadPayroll = () => {
    if (!selectedPeriodDates) return;
    setIsLoading(true);
    onPeriodSelected(selectedPeriodDates.start, selectedPeriodDates.end);
  };

  const canLoadPayroll = useMemo(() => {
    if (usingCustomRange) {
      return !!customStartDate && !!customEndDate && customStartDate <= customEndDate;
    }
    return selectedPeriodDates !== null;
  }, [usingCustomRange, customStartDate, customEndDate, selectedPeriodDates]);

  const canVoid = !usingCustomRange && selectedPeriodDates !== null && !isLoadingPeriods && !isVoiding;

  const handleVoid = async () => {
    if (!selectedPeriodDates || usingCustomRange) return;
    if (!confirmingVoid) {
      setConfirmingVoid(true);
      return;
    }
    try {
      setIsVoiding(true);
      await voidPayrollPeriod({
        businessId,
        periodStartDate: selectedPeriodDates.start,
        periodEndDate: selectedPeriodDates.end,
      });
      toast.success('Period voided');
      setConfirmingVoid(false);
      setReloadKey((key) => key + 1);
      onVoided?.();
    } catch (error) {
      console.error('Error voiding payroll period:', error);
      const message = error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
        ? error.message
        : 'Failed to void period';
      toast.error(message);
    } finally {
      setIsVoiding(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
      {payrollSettings ? (
        <>
          {isLoadingPeriods && (
            <p className="text-[13px] text-gray-500 px-1">Loading periods…</p>
          )}

          {!isLoadingPeriods && loadError && (
            <p className="text-[13px] text-gray-500 px-1">Couldn’t load payroll periods.</p>
          )}

          {!isLoadingPeriods && !loadError && periods.length === 0 && (
            <p className="text-[13px] text-gray-500 px-1">No periods have unpaid hours left.</p>
          )}

          {periods.map((period) => {
            const key = periodKey(period);
            const selected = selectedPeriod === key;
            const daysLeft = period.isCurrent ? daysLeftInPeriod(period.end) : null;
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setSelectedPeriod(key);
                  setConfirmingVoid(false);
                }}
                className={`w-full rounded-xl border p-4 text-left transition-colors touch-manipulation ${
                  selected
                    ? 'border-blue-300 bg-blue-50'
                    : 'border-gray-200 bg-white active:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    {daysLeft !== null && (
                      <p className="mb-1.5 text-[12px] text-gray-500">
                        <span className="mr-1 text-[15px] font-semibold tabular-nums text-gray-900">{daysLeft}</span>
                        {daysLeft === 1 ? 'day left in the period' : 'days left in the period'}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-gray-900">
                        {format(period.start, 'MMM d')} – {format(period.end, 'MMM d, yyyy')}
                      </span>
                      {period.isCurrent && (
                        <span className="inline-flex items-center rounded-md bg-blue-100 px-1.5 py-0.5 text-[11px] font-medium text-blue-700">
                          Current
                        </span>
                      )}
                      {period.isLast && (
                        <span className="inline-flex items-center rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                          Last
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-[13px] text-gray-500">
                      {period.resourceCount} {period.resourceCount === 1 ? 'resource' : 'resources'}
                      {' · '}
                      {hoursText(payrollMinutesFromHours(period.straightHours))} straight
                      {' · '}
                      {hoursText(payrollMinutesFromHours(period.overtimeHours))} OT
                    </p>
                    <p className="mt-1 text-[15px] font-semibold text-gray-900 tabular-nums">
                      ${period.totalEarnings.toFixed(2)}
                    </p>
                  </div>

                  <div className={`mt-0.5 w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center ${
                    selected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                  }`}>
                    {selected && <div className="w-2 h-2 bg-white rounded-full" />}
                  </div>
                </div>
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => {
              setSelectedPeriod('custom');
              setConfirmingVoid(false);
            }}
            className={`w-full rounded-xl border p-4 text-left transition-colors touch-manipulation ${
              selectedPeriod === 'custom'
                ? 'border-blue-300 bg-blue-50'
                : 'border-gray-200 bg-white active:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-gray-400" />
                <span className="font-semibold text-gray-900">Custom range</span>
              </div>
              <div className={`w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center ${
                selectedPeriod === 'custom' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
              }`}>
                {selectedPeriod === 'custom' && <div className="w-2 h-2 bg-white rounded-full" />}
              </div>
            </div>
          </button>

          {selectedPeriod === 'custom' && (
            <div className="bg-white p-4 rounded-xl border border-gray-200 space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1.5">
                  Start date
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1.5">
                  End date
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white p-4 rounded-xl border border-gray-200 space-y-4">
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-gray-400" />
            <span className="font-semibold text-gray-900">Select date range</span>
          </div>
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1.5">Start date</label>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1.5">End date</label>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}
      </div>

      <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3">
        {confirmingVoid && (
          <p className="mb-2 text-[13px] text-gray-500">Unpaid hours are stamped voided and will not be paid. People already paid in this period stay paid.</p>
        )}
        <div className="flex gap-2">
        {confirmingVoid ? (
          <>
            <button
              type="button"
              onClick={() => setConfirmingVoid(false)}
              disabled={isVoiding}
              className="rounded-xl bg-gray-100 px-4 py-2.5 text-[15px] font-semibold text-gray-700 active:bg-gray-200 touch-manipulation"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleVoid}
              disabled={isVoiding}
              className="min-w-0 flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-[15px] font-semibold text-white active:bg-red-700 touch-manipulation disabled:bg-gray-200 disabled:text-gray-500"
            >
              {isVoiding ? 'Voiding...' : 'Confirm void'}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={handleVoid}
              disabled={!canVoid}
              className={`rounded-xl px-4 py-2.5 text-[15px] font-semibold touch-manipulation ${
                canVoid
                  ? 'bg-red-600 text-white active:bg-red-700'
                  : 'bg-red-100 text-red-300 cursor-not-allowed'
              }`}
            >
              Void
            </button>
            <button
              type="button"
              onClick={handleLoadPayroll}
              disabled={!canLoadPayroll || isLoading || isLoadingPeriods}
              className={`min-w-0 flex-1 rounded-xl py-2.5 px-5 text-[15px] font-semibold transition-colors touch-manipulation ${
                canLoadPayroll && !isLoading && !isLoadingPeriods
                  ? 'bg-blue-600 text-white active:bg-blue-800'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isLoading ? 'Loading...' : 'Load Payroll Data'}
            </button>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
